import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithPopup,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { initializeAppCheck, ReCaptchaV3Provider, getToken } from "firebase/app-check";
import { billingWebConfig, isFirebaseConfigured } from "./billingConfig";

let app = null;
let auth = null;
let appCheck = null;
let appCheckInitAttempted = false;
let persistenceReady = null;

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

/** Map Firebase Auth errors to actionable copy (no secrets). */
export function formatAuthError(err) {
  const code = err?.code || "";
  if (code === "auth/operation-not-allowed") {
    return "Google sign-in is not enabled for this site yet. Try again later or contact support.";
  }
  if (code === "auth/popup-blocked") {
    return "The sign-in popup was blocked. Allow popups for this site and try again.";
  }
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
    return "Sign-in was cancelled.";
  }
  if (code === "auth/network-request-failed") {
    return "Network error during sign-in. Check your connection and try again.";
  }
  if (code === "auth/user-disabled") {
    return "This account is disabled.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Wait a moment and try again.";
  }
  return "Sign-in failed. Use the same Google account as the Stratify app.";
}

export function getFirebaseApp() {
  if (!isFirebaseConfigured()) return null;
  if (app) return app;
  if (getApps().length) {
    app = getApps()[0];
    return app;
  }
  app = initializeApp(billingWebConfig.firebase);
  return app;
}

export function getFirebaseAuth() {
  const instance = getFirebaseApp();
  if (!instance) return null;
  if (!auth) {
    auth = getAuth(instance);
    persistenceReady = setPersistence(auth, browserLocalPersistence).catch(() => null);
  }
  return auth;
}

export async function ensureAuthPersistence() {
  getFirebaseAuth();
  if (persistenceReady) await persistenceReady;
}

function ensureAppCheck() {
  if (appCheck || appCheckInitAttempted) return appCheck;
  appCheckInitAttempted = true;
  const firebaseApp = getFirebaseApp();
  const siteKey = billingWebConfig.appCheckSiteKey;
  if (!firebaseApp || !siteKey) return null;
  try {
    appCheck = initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch {
    appCheck = null;
  }
  return appCheck;
}

export async function getAppCheckToken() {
  const instance = ensureAppCheck();
  if (!instance) return null;
  try {
    const result = await getToken(instance, false);
    return result?.token || null;
  } catch {
    return null;
  }
}

export async function getIdToken({ forceRefresh = false } = {}) {
  const instance = getFirebaseAuth();
  const user = instance?.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(forceRefresh);
  } catch {
    return null;
  }
}

/** Cached SDK token. Callers should retry once with forceRefresh on 401. */
export async function getFreshIdToken() {
  return getIdToken({ forceRefresh: false });
}

export function subscribeAuth(callback) {
  const instance = getFirebaseAuth();
  if (!instance) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(instance, callback);
}

export function subscribeIdToken(callback) {
  const instance = getFirebaseAuth();
  if (!instance) return () => {};
  return onIdTokenChanged(instance, callback);
}

export async function signInWithGoogle() {
  const instance = getFirebaseAuth();
  if (!instance) throw new Error("Firebase Auth is not configured");
  await ensureAuthPersistence();
  return signInWithPopup(instance, googleProvider);
}

export async function reauthenticateWithGoogle() {
  const instance = getFirebaseAuth();
  const user = instance?.currentUser;
  if (!instance || !user) throw new Error("signed_out");
  return reauthenticateWithPopup(user, googleProvider);
}

export async function signOutUser() {
  const instance = getFirebaseAuth();
  if (!instance) return;
  await signOut(instance);
}

export function currentUser() {
  return getFirebaseAuth()?.currentUser || null;
}
